import {
  BackendDidNotMatchSchemaError,
  BillingPortalSessionError,
  CancelSubscriptionError,
  CheckoutSessionError,
  FetchingSubscriptionsError,
  SubscriptionErrorMessages,
  SubscriptionNotFoundError,
} from "@/domain/errors/subscription";
import { ServiceLogger } from "@/application/services/logger";

import type { ISubscriptionServiceInterface } from "@/application/abstractions/subscription/subscription-service.interface";
import { SubscriptionApiRepository } from "@/infrastructure/repositories/api/subscription/subscription-api.repository";
import { err, ok, match } from "@/lib/result-pattern";
import { ValidationError } from "@/domain/errors/common";
import { TaggedNetworkError } from "@/domain/errors/network";

function handleInvalidSchemaError(error: ValidationError) {
  ServiceLogger.error(
    SubscriptionErrorMessages.BackendDidNotMatchSchema.internal,
    error
  );
  return err(new BackendDidNotMatchSchemaError(error));
}

const SubscriptionService: ISubscriptionServiceInterface = {
  async getUserSubscription() {
    const result = await SubscriptionApiRepository.getSubscriptions();

    return match(
      result,
      (subs) => {
        if (subs.length <= 0) {
          ServiceLogger.warn(
            SubscriptionErrorMessages.SubscriptionNotFound.internal
          );
          return err(
            new SubscriptionNotFoundError(
              new TaggedNetworkError(404, "Empty subscription list")
            )
          );
        }
        return ok(subs[0]);
      },
      (error) => {
        if (error._tag === "ValidationError") {
          return handleInvalidSchemaError(error);
        }

        if (error.status === 404) {
          ServiceLogger.warn(
            SubscriptionErrorMessages.SubscriptionNotFound.internal
          );
          return err(new SubscriptionNotFoundError(error));
        }

        ServiceLogger.error(
          SubscriptionErrorMessages.FetchingError.internal,
          error
        );

        return err(new FetchingSubscriptionsError(error));
      }
    );
  },

  async createCheckoutSession(type) {
    const result = await SubscriptionApiRepository.createCheckoutSession(type);

    return match(result, ok, (error) => {
      if (error._tag === "ValidationError") {
        return handleInvalidSchemaError(error);
      }

      ServiceLogger.error(
        SubscriptionErrorMessages.CheckoutSessionError.internal,
        error
      );

      if (error._tag === "SubscriptionAlreadyActiveError") return err(error);

      return err(new CheckoutSessionError(error));
    });
  },

  async cancelSubscription() {
    const result = await SubscriptionApiRepository.cancelSubscription();

    return match(result, ok, (error) => {
      if (error._tag === "ValidationError") {
        return handleInvalidSchemaError(error);
      }

      ServiceLogger.error(
        SubscriptionErrorMessages.CancelSubscriptionError.internal,
        error
      );

      return err(new CancelSubscriptionError(error));
    });
  },

  async createBillingPortalSession() {
    const result =
      await SubscriptionApiRepository.createBillingPortalSession();

    return match(result, ok, (error) => {
      if (error._tag === "ValidationError") {
        return handleInvalidSchemaError(error);
      }

      ServiceLogger.error(
        SubscriptionErrorMessages.BillingPortalSessionError.internal,
        error
      );

      return err(new BillingPortalSessionError(error));
    });
  },
};

export { SubscriptionService };
